// Wave 20.44 — Extended (multi-day) forecast tiles.
//
// Sits below the existing 8-hour hourly panel in WeatherPanel /
// WeatherSurface. Fetches ONE forecast per future day (noon local)
// out to the tier's weatherHorizonHours; renders each as a compact
// tile (day name, condition icon, temp, wind). Free tier sees 3
// days of data (today from the hourly panel above + 2 daily tiles
// here) with an upgrade CTA to unlock 8 days.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import CloudIcon from '@mui/icons-material/Cloud';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link as RouterLink } from 'react-router-dom';
import { colors } from '../theme';
import { fmtSpeed, fmtTemp, useUnitSystem } from '../services/useUnitSystem';
import { useSubscription } from '../services/useSubscription';

// Wave 20.49dh — Weather goes through the api proxy.
const API_BASE = process.env.REACT_APP_API || 'https://api.aero.chat';
const WEATHER_API = `${API_BASE}/weather`;
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

// Server-known cap. UI clamps for both tiers so an unexpectedly
// large usage.weatherHorizonHours doesn't produce hundreds of tiles.
const MAX_TILES = 7; // day +1 through day +7 → 8 days total incl. today

interface Point {
  temperature_c: number | null;
  humidity_pct: number | null;
  precip_mm_1h: number | null;
  wind_speed_ms: number | null;
  wind_from_deg: number | null;
  when: string;
}

interface Props {
  coords: { lat: number; lng: number } | null;
}

/** Round a Date to the next 12:00 local time N days out. */
function daysOutNoon(offsetDays: number): Date {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  d.setHours(12, 0, 0, 0);
  return d;
}

function conditionOf(p: Point | null): 'clear' | 'cloudy' | 'rain' | 'snow' {
  if (!p) return 'clear';
  if ((p.precip_mm_1h ?? 0) > 0.1) {
    return (p.temperature_c ?? 10) <= 0 ? 'snow' : 'rain';
  }
  return (p.humidity_pct ?? 0) > 80 ? 'cloudy' : 'clear';
}
function IconFor({ cond, sx }: { cond: string; sx?: any }) {
  const Component =
    cond === 'rain' ? WaterDropIcon : cond === 'snow' ? AcUnitIcon : cond === 'cloudy' ? CloudIcon : WbSunnyIcon;
  return <Component sx={sx} />;
}

export default function ExtendedForecast({ coords }: Props) {
  const units = useUnitSystem();
  const { usage } = useSubscription();
  // horizonDays counts the FULL horizon incl. today; we render
  // one tile per FUTURE day, so tiles = horizonDays - 1.
  const horizonHours = usage?.weatherHorizonHours ?? 8;
  const horizonDays = Math.max(1, Math.round(horizonHours / 24));
  const unlockedTiles = Math.min(MAX_TILES, Math.max(0, horizonDays - 1));
  const lockedTiles = Math.max(0, MAX_TILES - unlockedTiles);
  const isFree = usage?.isFree ?? false;

  const [days, setDays] = useState<Array<Point | null>>([]);

  const load = useCallback(async () => {
    if (!coords || unlockedTiles === 0) {
      setDays([]);
      return;
    }
    const jwt = localStorage.getItem(TOKEN_KEY);
    if (!jwt) return;
    const points = Array.from({ length: unlockedTiles }, (_, i) => ({
      lat: coords.lat,
      lng: coords.lng,
      t: daysOutNoon(i + 1).toISOString(),
    }));
    try {
      const res = await fetch(`${WEATHER_API}/thrucast/batch`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ points }),
      });
      if (!res.ok) return;
      const body = await res.json();
      setDays(body.forecasts ?? []);
    } catch {
      /* silent — the hourly panel still renders */
    }
  }, [coords, unlockedTiles]);

  useEffect(() => {
    load();
    const id = window.setInterval(load, 30 * 60 * 1000);
    return () => window.clearInterval(id);
  }, [load]);

  const dayLabel = useMemo(
    () => (offsetDays: number) => {
      const d = new Date();
      d.setDate(d.getDate() + offsetDays);
      return d.toLocaleDateString(undefined, { weekday: 'short' });
    },
    [],
  );

  if (!coords) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="baseline" spacing={1}>
        <Typography sx={{ color: colors.brand, fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.18em' }}>
          EXTENDED FORECAST
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ color: colors.textMuted, fontSize: '0.7rem' }}>
          {isFree ? `${horizonDays} days` : `${horizonDays} days`}
        </Typography>
      </Stack>
      <Box
        sx={{
          mt: 1,
          display: 'grid',
          gridTemplateColumns: `repeat(${MAX_TILES}, minmax(0, 1fr))`,
          gap: 0.75,
        }}
      >
        {Array.from({ length: MAX_TILES }, (_, i) => {
          const offset = i + 1;
          const unlocked = i < unlockedTiles;
          const point = unlocked ? days[i] ?? null : null;
          const cond = conditionOf(point);
          return (
            <Box
              key={i}
              sx={{
                py: 1,
                px: 0.5,
                borderRadius: 2,
                backgroundColor: unlocked ? colors.surfaceElevated : 'rgba(255,255,255,0.02)',
                border: `1px ${unlocked ? 'solid' : 'dashed'} ${colors.border}`,
                textAlign: 'center',
                opacity: unlocked ? 1 : 0.5,
                minHeight: 78,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.25,
              }}
            >
              <Typography sx={{ fontSize: '0.6rem', color: colors.textMuted, fontWeight: 700, letterSpacing: '0.08em' }}>
                {dayLabel(offset)}
              </Typography>
              {unlocked ? (
                <>
                  <IconFor cond={cond} sx={{ color: colors.brand, fontSize: 16 }} />
                  <Typography
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '0.78rem',
                      color: colors.text,
                      fontWeight: 800,
                    }}
                  >
                    {point?.temperature_c != null ? fmtTemp(point.temperature_c, units) : '—'}
                  </Typography>
                  {point?.wind_speed_ms != null && (
                    <Typography sx={{ fontSize: '0.55rem', color: colors.textMuted, fontFamily: '"JetBrains Mono", monospace' }}>
                      {fmtSpeed(point.wind_speed_ms, units)}
                    </Typography>
                  )}
                </>
              ) : (
                <LockIcon sx={{ color: colors.textFaint, fontSize: 14, mt: 0.5 }} />
              )}
            </Box>
          );
        })}
      </Box>
      {isFree && lockedTiles > 0 && (
        <Box
          sx={{
            mt: 1.25,
            p: 1.25,
            borderRadius: 2,
            backgroundColor: `${colors.brand}0e`,
            border: `1px solid ${colors.brand}44`,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Chip
            label="FREE"
            size="small"
            sx={{
              backgroundColor: `${colors.brand}22`,
              color: colors.brand,
              fontWeight: 800,
              fontSize: '0.6rem',
              letterSpacing: '0.08em',
              height: 20,
            }}
          />
          <Typography sx={{ fontSize: '0.75rem', color: colors.text, fontWeight: 700, flex: 1, lineHeight: 1.3 }}>
            {horizonDays}-day forecast. Unlock the full 8-day forecast.
          </Typography>
          <Button
            component={RouterLink}
            to="/subscription"
            endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
            sx={{
              color: colors.brand,
              fontWeight: 800,
              fontSize: '0.72rem',
              px: 1,
              py: 0.25,
              minWidth: 0,
              '&:hover': { color: colors.brandBright, backgroundColor: 'transparent' },
            }}
          >
            Upgrade
          </Button>
        </Box>
      )}
    </Box>
  );
}
